import { useCallback, useMemo, useState } from 'react';

import { type Address } from 'viem';
import { type BaseError, useConnect, useConnections } from 'wagmi';
import { injected } from 'wagmi/connectors';
import { useShallow } from 'zustand/react/shallow';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { usePetraWallet } from '@pkg/hooks/wallets/usePetraWallet';
import { walletConnectConnector } from '@pkg/providers';

import { useWalletStore } from '@app/store/zustand';

export const useWalletConnections = () => {
    const connectedWallets = useWalletStore(useShallow((state) => state.connectedWallets));
    const toggleShowOverview = useWalletStore().toggleShowOverview;
    const { addToast } = useToastStore();
    const { handlePetraConnect } = usePetraWallet();
    const { connectAsync } = useConnect();
    const connections = useConnections();

    const [connectedAccount, setConnectedAccount] = useState<Address | undefined>();

    const handleAccountError = useCallback(
        (e) => {
            const error = e as BaseError;
            if (error.name === 'UserRejectedRequestError' || error.message.includes('User rejected the request')) {
                const errorMessage = 'You rejected the request in your wallet.';
                addToast({ message: errorMessage, timeout: 3000 });
            }
        },
        [addToast],
    );
    const activeConnectionsAddresses = useMemo(() => connections.flatMap(({ accounts }) => accounts), [connections]);
    const connectedAccountsAddresses = useMemo(
        () => connectedWallets?.map(({ address }) => address.split('.').pop()),
        [connectedWallets],
    );
    const matchingConnectedAccounts = useMemo(() => {
        if (activeConnectionsAddresses?.length && connectedAccountsAddresses?.length) {
            return activeConnectionsAddresses.filter((address) => {
                return !!connectedAccountsAddresses.find((add) => add.toLowerCase() == address.toLowerCase());
            });
        }
    }, [activeConnectionsAddresses, connectedAccountsAddresses]);

    const handleConnection = useCallback(
        async (address: string) => {
            const matchingConnection = connections?.find((connection) => {
                const matchingAccount = connection.accounts?.find(
                    (acc) => acc.toLowerCase() === address?.toLowerCase(),
                );
                return !!matchingAccount;
            });
            if (connections) {
                const foundConnector = matchingConnection?.connector?.name;
                const connector =
                    foundConnector === 'MetaMask' ? injected({ target: 'metaMask' }) : walletConnectConnector;
                if (connector) {
                    try {
                        const connectionRes = await connectAsync({ connector: connector });
                        if (connectionRes) {
                            setConnectedAccount(connectionRes.accounts?.[0]);
                            return connectionRes.accounts?.[0];
                        }
                    } catch (e) {
                        handleAccountError(e);
                    }
                }
            } else {
                // TODO: CHANGE to select connector
                toggleShowOverview(true);
                return false;
            }
        },
        [connectAsync, connections, handleAccountError, toggleShowOverview],
    );

    const getConnectedAccount = useCallback(
        async (prefixedAccount: string) => {
            const [chain, address] = prefixedAccount.split('.');
            if (chain === 'aptos') {
                return handlePetraConnect()
                    .then((r) => {
                        if (r) {
                            setConnectedAccount(r.address as Address);
                            return r.address;
                        }
                    })
                    .catch(handleAccountError);
            }
            const foundConnection = matchingConnectedAccounts?.find(
                (account) => account.toLowerCase() === address.toLowerCase(),
            );
            if (foundConnection) {
                setConnectedAccount(foundConnection as Address);
                return foundConnection;
            }

            return handleConnection(address)
                .then((r) => {
                    if (r) {
                        return r;
                    }
                })
                .catch(handleAccountError);
        },
        [handleAccountError, handleConnection, handlePetraConnect, matchingConnectedAccounts],
    );

    return {
        getConnectedAccount,
        connectedAccount: connectedAccount || matchingConnectedAccounts?.[0],
    };
};
